import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AuditLogEntry } from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import Alert from '../../components/Alert';
import Card from '../../components/Card';
import PageHeader from '../../components/PageHeader';
import {
  listAudit,
  type AuditListResponse,
  type ListAuditParams,
} from '../../services/audit';
import { ApiError } from '../../services/api';
import { formatDateTime } from '../../utils/date';
import { useGameStore } from '../../stores/gameStore';
import { adminModuleFor } from '../../games/registry';
import type { AdminGameModule } from '../../games/types';

/**
 * Map an audit `targetType` to the SPA route that can render it, so the
 * target id becomes a clickable deep-link. Platform types are mapped here;
 * a game's own audited entities come from the active game's admin manifest
 * (`auditTargetRoutes`, #817 — e.g. Word Clash's `question`). Returns `null`
 * for types we don't surface in the UI — render plain text in that case.
 */
function targetHref(
  targetType: string,
  targetId: string,
  gameRoutes: AdminGameModule['auditTargetRoutes'],
): string | null {
  switch (targetType) {
    case 'user':
      return `/users/${encodeURIComponent(targetId)}`;
    case 'match':
      return `/matches/${encodeURIComponent(targetId)}`;
    default:
      return gameRoutes?.[targetType]?.(targetId) ?? null;
  }
}

const PAGE_SIZE = 50;
const FILTER_DEBOUNCE_MS = 300;

interface FilterState {
  actorId: string;
  targetType: string;
  targetId: string;
  /** Datetime-local value (browser-local, no timezone). Converted to ISO on send. */
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = {
  actorId: '',
  targetType: '',
  targetId: '',
  from: '',
  to: '',
};

function parsePage(raw: string | null): number {
  if (!raw) return 1;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function filtersFromParams(params: URLSearchParams): FilterState {
  return {
    actorId: params.get('actorId') ?? '',
    targetType: params.get('targetType') ?? '',
    targetId: params.get('targetId') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
}

/**
 * Convert a `datetime-local` input value ("YYYY-MM-DDTHH:mm") into a full ISO
 * timestamp the server accepts. Returns `undefined` for empty input so the
 * query-string builder omits the key.
 */
function datetimeLocalToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/**
 * Admin audit-log viewer. Filter state is persisted to the URL query string
 * so admins can bookmark / share a specific investigation view (e.g.
 * "all user.ban events against user X in the last week"). The refetch key
 * covers every filter + page so changing any input triggers a fresh load.
 *
 * The table shows the compact row summary; clicking a row toggles an inline
 * JSON panel with the full before/after snapshots. For machine-readable
 * consumption, admins can still hit GET /api/admin/audit directly.
 */
export default function AuditList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeGameId = useGameStore((s) => s.activeGameId);
  const gameTargetRoutes = adminModuleFor(activeGameId)?.auditTargetRoutes;

  // `committed` drives the query; `draft` drives the form inputs. Debounced
  // commit lets admins type without hammering the API on every keystroke.
  const [draft, setDraft] = useState<FilterState>(() =>
    filtersFromParams(searchParams),
  );
  const committed = useMemo(
    () => filtersFromParams(searchParams),
    [searchParams],
  );
  const page = parsePage(searchParams.get('page'));

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AuditListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Keep draft in sync when the URL changes via back/forward navigation.
  useEffect(() => {
    setDraft(filtersFromParams(searchParams));
  }, [searchParams]);

  // Debounce draft → URL so page doesn't refetch per keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const syncKey = (key: keyof FilterState) => {
          const v = draft[key].trim();
          if (v) next.set(key, v);
          else next.delete(key);
        };
        syncKey('actorId');
        syncKey('targetType');
        syncKey('targetId');
        syncKey('from');
        syncKey('to');
        // Filter changes reset pagination — staying on p7 while switching
        // actorId would almost always land on an empty page.
        const draftEqualsCommitted =
          draft.actorId === committed.actorId &&
          draft.targetType === committed.targetType &&
          draft.targetId === committed.targetId &&
          draft.from === committed.from &&
          draft.to === committed.to;
        if (!draftEqualsCommitted) next.delete('page');
        return next;
      });
    }, FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, committed, setSearchParams]);

  const reqCounterRef = useRef(0);
  const fetchAudit = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const params: ListAuditParams = {
        actorId: committed.actorId.trim() || undefined,
        targetType: committed.targetType.trim() || undefined,
        targetId: committed.targetId.trim() || undefined,
        from: datetimeLocalToIso(committed.from),
        to: datetimeLocalToIso(committed.to),
        page,
        pageSize: PAGE_SIZE,
      };
      const data = await listAudit(params);
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load audit log';
      setState({ status: 'error', message });
    }
  }, [committed, page]);

  useEffect(() => {
    void fetchAudit();
  }, [fetchAudit]);

  function handlePageChange(next: number) {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params, { replace: true });
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  const rows = state.status === 'ready' ? state.data.data : [];

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    // Clamp to the server's `maxPage`. `total` is the TRUE match count and
    // stays honest, but the audit union over-fetches and merges rather than
    // using skip(), so it refuses to serve past a bounded window — deriving
    // the pager from `total` alone offered buttons that 400.
    const derived = Math.ceil(state.data.total / state.data.pageSize);
    const cap = state.data.maxPage ?? derived;
    return Math.max(1, Math.min(derived, cap));
  }, [state]);

  const columns: DataTableColumn<AuditLogEntry>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'When',
        render: (e) => (
          <span className="text-xs text-slate-600 whitespace-nowrap">
            {formatDateTime(e.createdAt)}
          </span>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        render: (e) => (
          <span className="font-mono text-xs text-slate-900">{e.action}</span>
        ),
      },
      {
        key: 'actor',
        header: 'Actor',
        render: (e) => {
          // Prefer the human-readable handle resolved server-side; fall back
          // to the raw ObjectId when the actor row no longer exists (e.g. a
          // deleted admin). Actors may be admins OR players, so we don't
          // assume an /admin-users link target — show plain text with the id
          // as a tooltip + secondary line.
          const handle = e.actorDisplayName ?? e.actorUsername;
          return (
            <div className="flex flex-col gap-0.5" title={e.actorId}>
              {handle ? (
                <>
                  <span className="text-xs font-medium text-slate-800 break-all">
                    {handle}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400 break-all">
                    {e.actorId}
                  </span>
                </>
              ) : (
                <span className="font-mono text-xs text-slate-700 break-all">
                  {e.actorId}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'target',
        header: 'Target',
        render: (e) => {
          const href = targetHref(e.targetType, e.targetId, gameTargetRoutes);
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-slate-600 uppercase tracking-wide">
                {e.targetType}
              </span>
              {href ? (
                <Link
                  to={href}
                  className="font-mono text-xs text-slate-700 break-all hover:text-slate-900 hover:underline"
                  title={e.targetId}
                >
                  {e.targetId}
                </Link>
              ) : (
                <span
                  className="font-mono text-xs text-slate-700 break-all"
                  title={e.targetId}
                >
                  {e.targetId}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'ip',
        header: 'IP / UA',
        render: (e) => (
          <div className="flex flex-col gap-0.5 max-w-[14rem]">
            <span className="font-mono text-xs text-slate-700 break-all">
              {e.ip ?? '—'}
            </span>
            <span
              className="text-xs text-slate-500 truncate"
              title={e.userAgent ?? undefined}
            >
              {e.userAgent ?? ''}
            </span>
          </div>
        ),
      },
      {
        key: 'expand',
        header: '',
        render: (e) => (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              setExpandedId((id) => (id === e.id ? null : e.id));
            }}
            data-testid={`audit-expand-${e.id}`}
            className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            {expandedId === e.id ? 'Hide diff' : 'Show diff'}
          </button>
        ),
      },
    ],
    [expandedId, gameTargetRoutes],
  );

  const expandedEntry =
    rows.find((r) => r.id === expandedId) ?? null;

  return (
    <div className="space-y-4" data-testid="audit-list-screen">
      <PageHeader
        title="Audit Log"
        actions={
          <button
            type="button"
            onClick={clearFilters}
            data-testid="audit-clear-filters"
            className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Clear filters
          </button>
        }
      />

      <Card
        padding="sm"
        className="grid grid-cols-1 md:grid-cols-5 gap-3"
      >
        <FilterInput
          label="Actor ID"
          value={draft.actorId}
          placeholder="AdminUser ObjectId"
          testId="audit-filter-actor-id"
          onChange={(v) => setDraft((d) => ({ ...d, actorId: v }))}
        />
        <FilterInput
          label="Target type"
          value={draft.targetType}
          placeholder="user | match | admin_user …"
          testId="audit-filter-target-type"
          onChange={(v) => setDraft((d) => ({ ...d, targetType: v }))}
        />
        <FilterInput
          label="Target ID"
          value={draft.targetId}
          placeholder="e.g. user/match ObjectId"
          testId="audit-filter-target-id"
          onChange={(v) => setDraft((d) => ({ ...d, targetId: v }))}
        />
        <FilterInput
          label="From"
          type="datetime-local"
          value={draft.from}
          testId="audit-filter-from"
          onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
        />
        <FilterInput
          label="To"
          type="datetime-local"
          value={draft.to}
          testId="audit-filter-to"
          onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
        />
      </Card>

      {state.status === 'error' && (
        <Alert testId="audit-list-error">{state.message}</Alert>
      )}

      <DataTable<AuditLogEntry>
        columns={columns}
        rows={rows}
        rowKey={(e) => e.id}
        loading={state.status === 'loading'}
        empty="No audit entries match these filters."
        testId="audit-table"
      />

      {expandedEntry && (
        <Card padding="md" data-testid="audit-diff-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-900">
              <span className="font-mono">{expandedEntry.action}</span> ·{' '}
              <span className="text-slate-500 text-xs">
                {formatDateTime(expandedEntry.createdAt)}
              </span>
            </h2>
            <button
              type="button"
              onClick={() => setExpandedId(null)}
              data-testid="audit-diff-close"
              className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <DiffColumn label="Before" value={expandedEntry.before} />
            <DiffColumn label="After" value={expandedEntry.after} />
          </div>
        </Card>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId: string;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-600">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
      />
    </label>
  );
}

function DiffColumn({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
        {label}
      </div>
      <pre className="text-xs font-mono bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
        {value == null ? 'null' : stringify(value)}
      </pre>
    </div>
  );
}

function stringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
